<template>
  <auth-shell>
    <auth-card
      ref="card"
      title="Приглашение в Комбу"
      :description="inviteDescription"
      error-id="invite-server-error"
      :error-message="visibleError"
      :loading="inviteLoading || submitting || acceptLoading"
      :loading-message="loadingMessage"
    >
      <template v-if="inviteLoaded && hasLoadedAccount && currentUserMatchesInvite">
        <q-btn
          unelevated
          no-caps
          color="primary"
          class="auth-submit"
          label="Принять приглашение"
          :loading="acceptLoading"
          :disable="acceptLoading"
          @click="onAcceptInvite"
        />
      </template>

      <q-form v-else-if="inviteLoaded && !hasLoadedAccount" class="auth-form" @submit.prevent="onSubmit">
        <p class="invite-email">Аккаунт будет создан для <strong>{{ email }}</strong>.</p>
        <password-input
          v-model="password"
          autocomplete="new-password"
          :disable="submitting"
          :describedby="visibleError ? 'invite-server-error' : ''"
          :rules="passwordRules"
        />
        <password-input
          v-model="confirmPassword"
          label="Подтвердите пароль"
          name="password-confirmation"
          autocomplete="new-password"
          :disable="submitting"
          :rules="confirmRules"
        />
        <q-checkbox
          v-model="agreeTerms"
          :disable="submitting"
          label="Я принимаю пользовательское соглашение"
        />
        <q-btn
          unelevated
          no-caps
          color="primary"
          class="auth-submit"
          label="Создать аккаунт"
          type="submit"
          :loading="submitting"
          :disable="submitting || !agreeTerms"
        />
      </q-form>

      <q-btn
        v-else-if="visibleError"
        flat
        no-caps
        color="primary"
        label="На страницу входа"
        :to="{ name: 'signin' }"
      />

      <template #footer>
        <p>Уже есть аккаунт? <router-link :to="{ name: 'signin' }">Войти</router-link></p>
      </template>
    </auth-card>
  </auth-shell>
</template>

<script>
import axios from 'axios'
import { AuthCard, AuthShell, PasswordInput } from '../../components/auth'
import { apiUrl } from '../../config'

const loadingTexts = [
  'Настраиваем окружение',
  'Договариваемся с налоговой',
  'Ищем нейробухгалтера',
  'Ловим связь с космической отчетностью'
]

export default {
  name: 'register_by_invite',
  components: { AuthCard, AuthShell, PasswordInput },
  data() {
    return {
      password: '',
      confirmPassword: '',
      agreeTerms: false,
      displayName: '',
      orgName: '',
      roleName: '',
      email: '',
      hasLoadedAccount: false,
      inviteLoaded: false,
      inviteLoading: true,
      inviteError: '',
      submitError: '',
      acceptLoading: false,
      submitting: false,
      currentUserMatchesInvite: false,
      registrationLoadingIndex: 0,
      registrationLoadingTimer: null,
      acceptHandler: null,
      connectionReadyHandler: null,
      connectionFailureHandler: null,
      connectionWaitTimer: null,
      passwordRules: [
        (value) => Boolean(value) || 'Введите пароль',
        (value) => value.length >= 8 || 'Пароль должен быть не короче 8 символов'
      ]
    }
  },
  computed: {
    accountMismatch() {
      return this.inviteLoaded && this.hasLoadedAccount && !this.currentUserMatchesInvite
        ? 'Вы вошли под другим email. Выйдите из аккаунта или запросите новое приглашение.'
        : ''
    },
    visibleError() {
      return this.submitError || this.inviteError || this.accountMismatch
    },
    inviteDescription() {
      if (!this.inviteLoaded) return 'Проверяем приглашение и учетную запись.'
      const invitation = `${this.displayName}, вас пригласили в организацию ${this.orgName} на рабочее место «${this.roleName.toLowerCase()}».`
      return this.hasLoadedAccount
        ? invitation
        : `${invitation} Для продолжения регистрации задайте пароль.`
    },
    loadingMessage() {
      if (this.inviteLoading) return 'Загружаем приглашение'
      if (this.acceptLoading) return 'Принимаем приглашение'
      return loadingTexts[this.registrationLoadingIndex] || loadingTexts[0]
    },
    confirmRules() {
      return [
        (value) => Boolean(value) || 'Подтвердите пароль',
        (value) => value === this.password || 'Пароли должны совпадать'
      ]
    }
  },
  created() {
    this.acceptHandler = (data) => {
      this.acceptLoading = false
      this.connection.updateOrgId(this.$q, data.org_id)
      this.$q.notify({ color: 'positive', message: 'Приглашение принято' })
      this.$router.push({ name: 'home' })
    }
    this.emitter.on('didAcceptInvite', this.acceptHandler)
  },
  mounted() {
    this.prepareInviteLoad()
  },
  beforeUnmount() {
    this.stopRegistrationLoading()
    this.clearConnectionWait()
    this.emitter.off('didAcceptInvite', this.acceptHandler)
  },
  methods: {
    prepareInviteLoad() {
      this.hasLoadedAccount = this.connection.hasAccount(this.$q)
      if (!this.hasLoadedAccount || this.connection.user_id !== undefined) {
        this.loadInviteInfo()
        return
      }

      this.inviteLoading = true
      this.connectionReadyHandler = () => {
        this.clearConnectionWait()
        this.loadInviteInfo()
      }
      this.connectionFailureHandler = () => {
        this.clearConnectionWait()
        this.inviteLoading = false
        this.inviteError = 'Не удалось проверить учетную запись. Войдите повторно.'
      }
      this.emitter.on('connectionReady', this.connectionReadyHandler)
      this.emitter.on('didReceiveAuthFail', this.connectionFailureHandler)
      this.connectionWaitTimer = window.setTimeout(this.connectionFailureHandler, 8000)
      if (!this.connection.makeConnection(this.$q)) this.connectionFailureHandler()
    },
    clearConnectionWait() {
      if (this.connectionReadyHandler) this.emitter.off('connectionReady', this.connectionReadyHandler)
      if (this.connectionFailureHandler) this.emitter.off('didReceiveAuthFail', this.connectionFailureHandler)
      if (this.connectionWaitTimer !== null) window.clearTimeout(this.connectionWaitTimer)
      this.connectionReadyHandler = null
      this.connectionFailureHandler = null
      this.connectionWaitTimer = null
    },
    async loadInviteInfo() {
      this.hasLoadedAccount = this.connection.hasAccount(this.$q)
      this.inviteLoading = true
      try {
        const response = await axios.post(`${apiUrl}/invite/get/`, {
          invite_token: this.$route.params.id,
          invite_id: this.$route.params.id,
          user_id: this.connection.user_id || -1
        })
        if (response.data.error !== undefined) {
          this.inviteError = 'Приглашение недействительно или уже использовано'
          return
        }
        const item = response.data.item
        this.orgName = item.organisation.display_name
        this.displayName = item.display_name
        this.email = item.email
        this.roleName = item.role.role
        this.currentUserMatchesInvite = response.data.current_user === true
        this.inviteLoaded = true
      } catch {
        this.inviteError = 'Приглашение не найдено'
      } finally {
        this.inviteLoading = false
      }
    },
    startRegistrationLoading() {
      this.registrationLoadingIndex = 0
      this.registrationLoadingTimer = window.setInterval(() => {
        this.registrationLoadingIndex = (this.registrationLoadingIndex + 1) % loadingTexts.length
      }, 2000)
    },
    stopRegistrationLoading() {
      if (this.registrationLoadingTimer !== null) window.clearInterval(this.registrationLoadingTimer)
      this.registrationLoadingTimer = null
    },
    showSubmitError(message) {
      this.submitError = message
      this.$refs.card.focusError()
    },
    inviteRegisterError(code) {
      if (code === 'invalid-invite') return 'Приглашение не найдено'
      if (code === 'invalid-email') return 'Такой email не существует'
      if (code === 'email-mismatch') return 'Email приглашения не совпадает с email регистрации'
      if (code === 'weak-password') return 'Пароль должен быть не короче 8 символов'
      return 'Не удалось создать аккаунт'
    },
    onAcceptInvite() {
      if (this.acceptLoading) return
      this.acceptLoading = true
      this.connection.acceptInvite(this.connection.makeRequestId('accept-invite'), this.$route.params.id)
    },
    async requestToken() {
      const response = await axios.post(`${apiUrl}/user/token/request/`, {}, {
        auth: { username: this.email, password: this.password }
      })
      if (response.data.token === undefined) throw new Error('missing-token')
      this.connection.updateCredentials(this.$q, response.data.token, response.data.token_uuid, true)
      this.connection.makeConnection(this.$q)
      this.$router.push({ name: 'profile' })
      if (response.data.user_info) this.emitter.emit('receiveUserInfo', response.data.user_info)
    },
    async onSubmit() {
      if (this.submitting || !this.agreeTerms) return
      this.submitting = true
      this.submitError = ''
      this.startRegistrationLoading()
      try {
        const response = await axios.post(`${apiUrl}/register/invite/`, {
          email: this.email,
          password: this.password,
          invite_token: this.$route.params.id,
          invite_id: this.$route.params.id
        })
        if (response.data.success !== 1) {
          this.password = ''
          this.confirmPassword = ''
          this.showSubmitError(this.inviteRegisterError(response.data.error))
          return
        }
        await this.requestToken()
      } catch {
        this.showSubmitError('Сервер не отвечает, повторите попытку позже')
      } finally {
        this.stopRegistrationLoading()
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.invite-email { margin: 0; color: var(--sid-color-text-secondary); line-height: var(--sid-line-height-body); }
</style>
